import {isUndefined} from 'lodash';
import React, {ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {RendererOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {ResourceMap} from '../../../../store/appTypes';
import {renderFilterContent, renderFilterIdsV2} from '../../../helpers/analyticsFiltersHelpers';

/*
 * Types.
 */

export interface FilterButtonSelectionLabels {
  singleItemLabel: ReactNode;
  multiItemLabel: ReactNode;
}

/*
 * Props.
 */

interface AnalyticsFilterButtonValueProps<T> {
  iconName?: IconName;
  selectedIds?: ReadonlyArray<keyof ResourceMap<T>>;
  itemsMap: ResourceMap<T>;
  label: ReactNode;
  renderItem?: RendererOf<T>;
  selectionLabels?: FilterButtonSelectionLabels;
  shouldLabelSelection?: boolean;
  shouldShrink?: boolean;
  isActive?: boolean;
}

/*
 * Constants.
 */

const defaultSelectionLabels: FilterButtonSelectionLabels = {
  singleItemLabel: <Trans id="analytics-filter-button-selected-is">Is</Trans>,
  multiItemLabel: <Trans id="analytics-filter-button-selected-any-of">Any of</Trans>,
};

/*
 * Style.
 */

const SelectionLabelSpan = styled.span`
  margin-right: 6px;
`;

/*
 * Component.
 */

export const AnalyticsFilterButtonValue = <T,>(props: AnalyticsFilterButtonValueProps<T>) => {
  const theme = useUserTheme();
  const {
    iconName,
    selectedIds,
    itemsMap,
    label,
    renderItem,
    selectionLabels,
    shouldLabelSelection = true,
    shouldShrink = false,
    isActive = false,
  } = props;

  const iconColor = isActive ? theme.palette.blue.shade40 : undefined;

  // If there aren't any selected id's we render the label, which for example could be
  // an empty state (such as "Teammates") or a special selection (such as "Without tags").
  if (isUndefined(selectedIds) || selectedIds.length === 0 || isUndefined(renderItem)) {
    return renderFilterContent(label, iconName, shouldShrink, iconColor);
  }

  const filterValuesForDisplay = renderFilterIdsV2({selectedIds, itemsMap, renderItem});

  // A labeled selection includes a modifier on the selected items, such as
  // "Is any of" or "Includes all of"
  if (shouldLabelSelection) {
    const {singleItemLabel, multiItemLabel} = selectionLabels ?? defaultSelectionLabels;

    const selectionLabel = selectedIds.length === 1 ? singleItemLabel : multiItemLabel;
    const labeledSelection = (
      <>
        <SelectionLabelSpan>{selectionLabel}</SelectionLabelSpan>
        {filterValuesForDisplay}
      </>
    );
    return renderFilterContent(labeledSelection, iconName, shouldShrink, iconColor);
  }

  // An unlabeled selection is just the renderer filter ids
  return renderFilterContent(filterValuesForDisplay, iconName, shouldShrink, iconColor);
};
